import React, { useState, useRef, useContext } from 'react';
import { Upload, Trash2, Check, PenTool, Sparkles, Loader2 } from 'lucide-react';
import { AuthContext } from '../../context/AuthContext';
import SignatureEditor from './SignatureEditor';
import heic2any from 'heic2any';

const SignatureManager = ({ hasSignature, signatureData, onUpdate, loading: externalLoading }) => {
    const { fetchWithAuth } = useContext(AuthContext);
    const [uploading, setUploading] = useState(false);
    const [converting, setConverting] = useState(false);
    const [editorImage, setEditorImage] = useState(null);
    const fileInputRef = useRef(null);
    
    // Combined loading state
    const isLoading = uploading || converting || externalLoading;

    // Cache buster for image
    const signatureUrl = signatureData && (signatureData.startsWith('data:') ? signatureData : `${signatureData}?t=${new Date().getTime()}`);

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const isHeic = file.type === 'image/heic' || 
                       file.type === 'image/heif' || 
                       file.name.toLowerCase().endsWith('.heic') || 
                       file.name.toLowerCase().endsWith('.heif');

        if (isHeic) {
            setConverting(true);
            try {
                // Convert HEIC to PNG for browser compatibility
                const convertedBlob = await heic2any({
                    blob: file,
                    toType: 'image/png',
                    quality: 0.9
                });
                
                // heic2any can return an array if multiple images are in one container
                const finalBlob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;
                const objectUrl = URL.createObjectURL(finalBlob);
                setEditorImage(objectUrl);
            } catch (err) {
                console.error('HEIC conversion failed:', err);
                alert('Heic-Konvertierung fehlgeschlagen. Bitte versuch ein anderes Format.');
            } finally {
                setConverting(false);
            }
        } else {
            // Standard handling for non-HEIC images
            const objectUrl = URL.createObjectURL(file);
            setEditorImage(objectUrl);
        }
        
        // Reset input for next selection
        e.target.value = '';
    };

    const handleEditorSave = async (processedBase64) => {
        setEditorImage(null);
        setUploading(true);

        const formData = new FormData();
        formData.append('signature_base64', processedBase64);

        try {
            const response = await fetchWithAuth('/api/profile/signature/', {
                method: 'POST',
                body: formData
            });
            
            if (response.ok) {
                if (onUpdate) onUpdate();
            } else {
                let errorData;
                try {
                    errorData = await response.json();
                } catch (e) {
                    errorData = { detail: 'Upload fehlgeschlagen' };
                }
                throw new Error(errorData.error || errorData.detail);
            }
        } catch (err) {
            console.error('Signature upload failed:', err);
            alert(`Fehler beim Speichern: ${err.message}`);
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="bg-white dark:bg-[#1a1a1a] rounded-3xl border border-gray-100 dark:border-white/5 p-8 shadow-sm hover:shadow-xl hover:shadow-gray-200/50 dark:hover:shadow-black/20 transition-all duration-500">
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                    <div className="p-3 bg-plantlix-500/10 text-plantlix-500 rounded-2xl">
                        <PenTool size={22} />
                    </div>
                    <div>
                        <h3 className="font-black text-gray-900 dark:text-white text-lg tracking-tight">Digitale Unterschrift</h3>
                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">Herbarium & Dokumente</p>
                    </div>
                </div>
                {hasSignature && (
                    <div className="flex items-center gap-1.5 text-[10px] font-black text-plantlix-500 bg-plantlix-500/10 px-3 py-1 rounded-full tracking-tighter">
                        <Check size={12} strokeWidth={3} />
                        AKTIV
                    </div>
                )}
            </div>

            <div className="relative group">
                <div className={`w-full h-44 border-2 border-dashed rounded-[2rem] flex flex-col items-center justify-center bg-gray-50/30 dark:bg-white/5 overflow-hidden transition-all duration-500 ${
                    hasSignature ? 'border-plantlix-500/20' : 'border-gray-200 dark:border-white/10 group-hover:border-plantlix-500/50'
                }`}>
                    {signatureData ? (
                        <div className="relative w-full h-full flex items-center justify-center p-8 bg-white dark:bg-[#1e1e1e]">
                            <img 
                                src={signatureData} 
                                alt="Unterschrift" 
                                className="max-h-full max-w-full object-contain filter dark:invert dark:contrast-125" 
                            />
                            <div className="absolute inset-0 bg-plantlix-500/0 group-hover:bg-plantlix-500/5 transition-colors duration-500 flex items-center justify-center gap-4">
                                <button 
                                    onClick={() => setEditorImage(signatureData)}
                                    className="p-4 bg-white dark:bg-gray-800 shadow-2xl text-plantlix-500 rounded-2xl opacity-0 group-hover:opacity-100 transform translate-y-4 group-hover:translate-y-0 transition-all duration-500 scale-90 group-hover:scale-100 flex items-center gap-2 font-black text-xs"
                                    title="Bearbeiten"
                                >
                                    <PenTool size={18} />
                                    <span>STUDIO</span>
                                </button>
                                <button 
                                    onClick={() => fileInputRef.current.click()}
                                    className="p-4 bg-white dark:bg-gray-800 shadow-2xl text-gray-400 hover:text-plantlix-500 rounded-2xl opacity-0 group-hover:opacity-100 transform translate-y-4 group-hover:translate-y-0 transition-all duration-500 scale-90 group-hover:scale-100"
                                    title="Neu hochladen"
                                >
                                    <Upload size={18} />
                                </button>
                            </div>
                        </div>
                    ) : (
                        <button 
                            onClick={() => fileInputRef.current.click()}
                            className="flex flex-col items-center text-gray-400 hover:text-plantlix-500 transition-all duration-500"
                        >
                            <div className="w-16 h-16 bg-gray-100 dark:bg-white/5 rounded-3xl flex items-center justify-center mb-4 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-sm">
                                <Upload size={28} />
                            </div>
                            <span className="text-sm font-black tracking-tight text-gray-600 dark:text-gray-300">Foto hochladen</span>
                            <span className="text-[10px] uppercase font-bold tracking-[0.2em] mt-2 opacity-40">PNG, JPG, HEIC</span>
                        </button>
                    )}
                </div>
                
                {isLoading && (
                    <div className="absolute inset-0 bg-white/90 dark:bg-gray-900/90 backdrop-blur-md flex items-center justify-center rounded-[2rem] z-20 animate-in fade-in duration-300">
                        <div className="flex flex-col items-center gap-4">
                            <div className={`w-10 h-10 border-4 ${converting ? 'border-orange-500' : 'border-plantlix-500'} border-t-transparent rounded-full animate-spin`}></div>
                            <span className={`text-xs font-black ${converting ? 'text-orange-600' : 'text-plantlix-500'} tracking-widest uppercase`}>
                                {converting ? 'Konvertiere HEIC...' : 'Verarbeite...'}
                            </span>
                        </div>
                    </div>
                )}
            </div>

            <input 
                type="file" 
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="image/*"
                className="hidden"
            />

            <div className="mt-6 p-5 bg-plantlix-500/5 dark:bg-plantlix-500/10 rounded-2xl border border-plantlix-500/10 flex gap-4">
                <div className="p-2 bg-plantlix-500/10 text-plantlix-500 rounded-xl flex-shrink-0 self-start">
                    <Sparkles size={16} />
                </div>
                <div className="text-[11px] text-plantlix-500/80 dark:text-plantlix-400/60 leading-relaxed font-medium">
                    Nutze das <span className="font-black text-plantlix-500">Signatur-Studio</span> für perfekte Transparenz. Deine Daten werden lokal verarbeitet und Ende-zu-Ende verschlüsselt gespeichert.
                </div>
            </div>

            {/* Editor Modal */}
            {editorImage && (
                <SignatureEditor 
                    image={editorImage}
                    onSave={handleEditorSave}
                    onCancel={() => setEditorImage(null)}
                />
            )}
        </div>
    );
};

export default SignatureManager;
